import React, {useState, Fragment, useEffect, useRef, useCallback} from "react";
import {
	createClient,
} from "agora-rtc-react";


import ChannelForm from "./ChannelForm";
import {VideoCall} from "./VideoCall";
import {AudioCall} from "./AudioCall";
import TranscriptionButton from "./TranscriptionButton";
import {useAgoraTranscription} from "./useAgoraTranscription";
import LiveTranscriptSubtitle from "./LiveTranscriptSubtitle";
import classes from "../../../components/pharmacyComponent/pharmacycomponent.module.css";
import { connect } from "react-redux";
import { endAgoraCall } from "../../../actions/ConversationAction";
import {database} from "../../../firebase";
import {markInAppCallEnded} from "../../../utils/inAppCallEnd";

const config = {mode: "rtc", codec: "vp8"};
const useClient = createClient(config);
const appId = "d8fe8fa3c9564b579b91a23b6c09d3d9";

const AgoraComponent = (props) => {
	const client = useClient();

	const [inCall, setInCall] = useState(false);
	const [channelName, setChannelName] = useState("");
	const [token, setToken] = useState("");
	const [callType, setCallType] = useState(null);

	// transcription wiring
	const [tracksReady, setTracksReady] = useState(false);
	const [transcriptionStarted, setTranscriptionStarted] = useState(false);
	const [isStartingTranscription, setIsStartingTranscription] = useState(false);
	const callInitializedRef = useRef(false);
	const isCleaningUpRef = useRef(false);
	const manuallyStoppedRef = useRef(false); // prevents auto-restart after manual stop
	const lastTracksRef = useRef(null); // stores last known local/remote tracks for restart
	const [callKey, setCallKey] = useState(0);

	// NEW: ensure we don't overlap "leave()" calls
	const ensureIdleRef = useRef(Promise.resolve());

	const ensureClientIdle = useCallback(() => {
		ensureIdleRef.current = ensureIdleRef.current.then(async () => {
			try {
				const state = client?.connectionState;
				if (state === "CONNECTED" || state === "CONNECTING") {
					try { await client.leave(); } catch (e) {}
				}
				try { client?.removeAllListeners?.(); } catch (e) {}
			} catch (e) {}
		});
		return ensureIdleRef.current;
	}, [client]);

	const { startTranscription, stopTranscription, isTranscribing, isLive, updateAudioStreams, liveTranscript, interimTranscript } =
		useAgoraTranscription({
			// The call's own chat, not whichever chat is open: switching chats mid-call used to stop
			// the transcript and lose the recording.
			conversationId: props?.callData?.conversationId || props?.conversationId,
			doctorDetail: props?.doctorDetail,
			callType,
			transcriptMessageKey: props?.callData?.transcriptMessageKey
		});

	useEffect(() => {
		if (token) setInCall(true);
	}, [token]);

	// REPLACE the existing "props.callData" hydration effect with token-based deps
	useEffect(() => {
		if (!props.callData) return;

		callInitializedRef.current = false;
		manuallyStoppedRef.current = false; // reset on new call
		setTranscriptionStarted(false);
		setTracksReady(false);

		setChannelName(props.callData?.channelName || "");
		setToken(props.callData?.rctToken || "");
		setCallType(props.callData?.callType || null);

		// force remount for every new token/call
		setCallKey((k) => k + 1);
	}, [props.callData?.rctToken, props.callData?.channelName, props.callData?.callType]); // <-- changed deps

	const handleAudioStreamsReady = useCallback((localTrack, remoteTrack) => {
		// Always persist the latest tracks so they can be replayed on transcription restart
		lastTracksRef.current = { localTrack, remoteTrack };
		if (transcriptionStarted) {
			updateAudioStreams(localTrack, remoteTrack);
			return;
		}
		setTracksReady({ localTrack, remoteTrack });
	}, [transcriptionStarted, updateAudioStreams]);

	useEffect(() => {
		if (!token) return;
		if (transcriptionStarted) return;
		if (callInitializedRef.current) return;
		if (isCleaningUpRef.current) return;
		if (manuallyStoppedRef.current) return; // don't auto-restart after manual stop

		callInitializedRef.current = true;
		startTranscription()
			.then(() => setTranscriptionStarted(true))
			.catch((e) => {
				callInitializedRef.current = false;
				console.error("startTranscription failed:", e);
			});
	}, [token, transcriptionStarted, startTranscription]);

	useEffect(() => {
		if (!transcriptionStarted) return;
		if (!tracksReady || tracksReady === true) return;
		if (typeof tracksReady !== "object") return;

		updateAudioStreams(tracksReady.localTrack, tracksReady.remoteTrack);
		setTracksReady(true);
	}, [transcriptionStarted, tracksReady, updateAudioStreams]);

	const handleCallEnd = useCallback(async () => {
		if (isCleaningUpRef.current) return;
		isCleaningUpRef.current = true;

		// Every ending, not only the red button, marks callLogs ended: left "in_call"/"available",
		// the patient app let the patient rejoin the dead call from the chat message.
		markInAppCallEnded(database, props?.callData?.conversationId || props?.conversationId);

		// unmount call UIs immediately so their cleanup runs
		setInCall(false);
		props?.endAgoraCall?.();

		try {
			// Always: stopTranscription is a no-op when nothing runs. The old `if (isTranscribing)`
			// read a stale value when the patient hung up first, so the transcript was never closed
			// and the recording never saved.
			await stopTranscription();
		} catch (e) {
			console.log("stopTranscription failed (continuing):", e?.message);
		} finally {
			// NEW: force client to become reusable for the next call
			await ensureClientIdle();

			setTranscriptionStarted(false);
			setTracksReady(false);

			setToken("");
			setChannelName("");
			setCallType(null);

			callInitializedRef.current = false;
			setCallKey((k) => k + 1);

			setTimeout(() => {
				isCleaningUpRef.current = false;
			}, 800);
		}
	}, [stopTranscription, ensureClientIdle, props?.endAgoraCall, props?.callData?.conversationId, props?.conversationId]);

	const setInCallFromChild = useCallback((next) => {
		if (next === false) {
			handleCallEnd();
			return;
		}
		setInCall(!!next);
	}, [handleCallEnd]);

	const handleStartTranscription = useCallback(async () => {
		if (isStartingTranscription || isCleaningUpRef.current) return;
		try {
			manuallyStoppedRef.current = false; // allow transcription to run
			setIsStartingTranscription(true);
			callInitializedRef.current = true;
			await startTranscription();
			setTranscriptionStarted(true);
			// Re-wire audio streams if tracks are already available (mid-call restart)
			if (lastTracksRef.current) {
				const { localTrack, remoteTrack } = lastTracksRef.current;
				console.log("Re-wiring audio streams after manual transcription restart");
				updateAudioStreams(localTrack, remoteTrack);
			}
		} catch (e) {
			callInitializedRef.current = false;
			console.error("startTranscription (manual) failed:", e);
		} finally {
			setIsStartingTranscription(false);
		}
	}, [isStartingTranscription, startTranscription, updateAudioStreams]);

	const handleStopTranscription = useCallback(async () => {
		try {
			manuallyStoppedRef.current = true; // block auto-restart
			await stopTranscription();
		} catch (error) {
			console.log("stopTranscription (manual) failed:", error?.message || error);
		} finally {
			setTranscriptionStarted(false);
			setTracksReady(false);
			callInitializedRef.current = false;
		}
	}, [stopTranscription]);

	useEffect(() => {
		return () => {
			try { stopTranscription?.(); } catch (e) {}
			ensureClientIdle();
		};
	}, [stopTranscription, ensureClientIdle]);

	useEffect(() => {
		const callActive = Boolean(inCall && props.callData);
		if (callActive) return;
		if (!isTranscribing) return;
		if (isCleaningUpRef.current) return;

		handleStopTranscription().catch(() => {});
	}, [inCall, props.callData, isTranscribing, handleStopTranscription]);

	const shouldRenderCallUI = !!props.callData && inCall && !!callType;

	// Tell the doctor when the transcript is not being captured. A short grace period hides the
	// normal second it takes to connect or reconnect.
	const [showTranscriptWarning, setShowTranscriptWarning] = useState(false);
	useEffect(() => {
		if (!shouldRenderCallUI || !isTranscribing || isLive) {
			setShowTranscriptWarning(false);
			return;
		}
		const timer = setTimeout(() => setShowTranscriptWarning(true), 4000);
		return () => clearTimeout(timer);
	}, [shouldRenderCallUI, isTranscribing, isLive]);

	return (
		<>
			{(shouldRenderCallUI || isTranscribing) && (
				<TranscriptionButton
					onStart={handleStartTranscription}
					onStop={handleStopTranscription}
					isTranscribing={isTranscribing}
					isStarting={isStartingTranscription}
					callActive={shouldRenderCallUI}
				/>
			)}

			{showTranscriptWarning && (
				<div role="alert" style={{ color: "#c62828", fontWeight: 600, margin: "6px 0" }}>
					Transcription is not running — reconnecting automatically. The call is still being recorded.
				</div>
			)}
			{shouldRenderCallUI && !isTranscribing && transcriptionStarted === false && manuallyStoppedRef.current && (
				<div role="status" style={{ color: "#b26a00", fontWeight: 600, margin: "6px 0" }}>
					Transcription is off for this call.
				</div>
			)}

			{/* Live Transcript Subtitle Display */}
			<LiveTranscriptSubtitle 
				liveTranscript={liveTranscript}
				interimTranscript={interimTranscript}
				isTranscribing={isTranscribing}
			/>

			{shouldRenderCallUI ? (
				<div className={classes.generalInformation}>
					<Fragment>
						<div className={classes.expandable_section}>
							<div className={classes.general_info_section}>
								{callType === "video" ? (
									<VideoCall
										key={`video-${callKey}`}
										setInCall={setInCallFromChild}
										channelName={channelName}
										client={client}
										appId={appId}
										token={token}
										account={1}
										onAudioStreamsReady={handleAudioStreamsReady}
										onRemoteHangup={handleCallEnd}
									/>
								) : (
									<AudioCall
										key={`audio-${callKey}`}
										setInCall={setInCallFromChild}
										channelName={channelName}
										client={client}
										appId={appId}
										token={token}
										account={1}
										onAudioStreamsReady={handleAudioStreamsReady}
										onRemoteHangup={handleCallEnd}
									/>
								)}
							</div>
						</div>
					</Fragment>
				</div>
			) : isTranscribing ? (
				<div className={classes.generalInformation}>
					<div className={classes.expandable_section}>
						<div className={classes.general_info_section}>
							<p>Transcription is still running. You need to click on the stop button above before starting a new call.</p>
						</div>
					</div>
				</div>
			) : (
				<div />
			)}
		</>
	);
};

const mapStateToProps = ({
							 conversationReducer,
							 doctorReducer
						 }) => {
    const {
        selectedPatient,
        selectedConversationObj,
        callData,
        conversationId,
    } = conversationReducer;

    const {
        doctorDetail,
    } = doctorReducer;

    return {
        selectedPatient,
        selectedConversationObj,
        conversationId,
        callData,
        doctorDetail
    };
};

export default connect(mapStateToProps, { endAgoraCall })(AgoraComponent);
